"use client";

import { useLingui } from "@lingui/react/macro";
import { PermitsIcon } from "@offstreetparking/design-system/icons/PermitsIcon";
import { ReceiptIcon } from "@offstreetparking/design-system/icons/ReceiptIcon";
import { UserIcon } from "@offstreetparking/design-system/icons/UserIcon";
import { classNames } from "@offstreetparking/design-system/utils";
import dynamic from "next/dynamic";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useParker } from "../../../providers/parker";
import { routes } from "../../../routes";
import { useKiosk } from "../../providers/KioskProvider";
const ActiveIndicator = dynamic(() => import("./ActiveIndicator").then((m) => m.ActiveIndicator), {
	ssr: false,
});
export const ELEMENT_ID = "bottom-bar";
export const BottomBar = ({ isInline = false }: { isInline?: boolean }) => {
	const { isKiosk } = useKiosk();
	const { t } = useLingui();
	const pathname = usePathname();
	const { isLoggedIn } = useParker();
	const { portalAuthFlag } = useFeatureFlags();
	if (!isLoggedIn || !portalAuthFlag || isKiosk) {
		return null;
	}
	return (
		<>
			{!isInline && <div aria-hidden className="h-24 md:hidden print:hidden" />}
			<nav
				id={ELEMENT_ID}
				aria-label={t`Account navigation`}
				className={classNames(
					isInline
						? "shrink-0 border-neutral-faded border-t bg-white"
						: "fixed right-0 bottom-0 left-0 z-footer border-neutral-faded border-t bg-white/95 backdrop-blur-sm",
					"md:hidden print:hidden",
				)}
			>
				<div className="grid grid-cols-3">
					<NextLink
						prefetch={false}
						href={routes.permits()}
						aria-current={pathname.includes(routes.permits()) ? "page" : undefined}
						className={classNames(
							"relative flex min-h-20 flex-col items-center justify-center gap-2 px-3 text-center transition-colors",
							!pathname.includes(routes.permits()) ? "hover:text-primary" : "",
						)}
					>
						{pathname.includes(routes.permits()) && (
							<ActiveIndicator
								layoutId="bottom-bar-active-indicator"
								className="absolute top-0 left-0 h-1 w-full bg-primary"
							/>
						)}
						<PermitsIcon className="h-6 w-6" />
						<span className="font-medium text-sm">{t`Permits`}</span>
					</NextLink>

					<NextLink
						prefetch={false}
						href={routes.transactions()}
						aria-current={pathname.includes(routes.transactions()) ? "page" : undefined}
						className={classNames(
							"relative flex min-h-20 flex-col items-center justify-center gap-2 px-3 text-center transition-colors",
							!pathname.includes(routes.transactions()) ? "hover:text-primary" : "",
						)}
					>
						{pathname.includes(routes.transactions()) && (
							<ActiveIndicator
								layoutId="bottom-bar-active-indicator"
								className="absolute top-0 left-0 h-1 w-full bg-primary"
							/>
						)}
						<ReceiptIcon className="h-6 w-6" />
						<span className="font-medium text-sm">{t`Transactions`}</span>
					</NextLink>

					<NextLink
						prefetch={false}
						href={routes.account()}
						aria-current={pathname.includes(routes.account()) ? "page" : undefined}
						className={classNames(
							"relative flex min-h-20 flex-col items-center justify-center gap-2 px-3 text-center transition-colors",
							!pathname.includes(routes.account()) ? "hover:text-primary" : "",
						)}
					>
						{pathname.includes(routes.account()) && (
							<ActiveIndicator
								layoutId="bottom-bar-active-indicator"
								className="absolute top-0 left-0 h-1 w-full bg-primary"
							/>
						)}
						<UserIcon className="h-6 w-6" />
						<span className="font-medium text-sm">{t`Account`}</span>
					</NextLink>
				</div>
			</nav>
		</>
	);
};
